import { padNormalizedRoi, roiFromJoints } from "./playerRoiFromJoints";
import defaultAppLogo from "../assets/smarttt-default-logo.png";
import {
  OUTPUT_FPS,
  OUTPUT_HEIGHT,
  OUTPUT_WIDTH,
  buildOutputTimeline,
  outputDurationSeconds,
  reelExportNeedsAudio,
  resolveReelOutputFps,
  segmentReelAudioEnabled,
  snapSourceFps,
  sourceFrameBucket,
  sourceSamplePeriodSec,
} from "./strokeSlowMoReel";
import {
  buildKneeOverlayState,
  drawKneeOverlayOnCanvas,
  kneeStateAtTime,
} from "./strokeSlowMoKneeOverlay";
import { evalVideoExtension } from "./evalVideoEncoding";
import { resolveClubImageUrl, resolveClubLogoUrl } from "./resolveClubImageUrl";
import { ensurePoseVideoBlobUrl, fetchProxiedRemoteBlobUrl } from "./videoFrameCapture";
import { hrAtVideoTime } from "./videoViewSetup";
import {
  canUseWebCodecsReelEncoder,
  encodeCanvasFramesToMp4,
} from "./reelWebCodecsEncoder";

const BASE_RECORD_BITRATE = 6_000_000;
const ASPECT = 9 / 16;

function reelBitrateForFps(fps) {
  const rate = Number(fps) > 0 ? Number(fps) : OUTPUT_FPS;
  return Math.round(BASE_RECORD_BITRATE * Math.max(1, rate / OUTPUT_FPS));
}
function clamp01(n) {
  return Math.min(1, Math.max(0, n));
}

function averageCropRect(rects) {
  if (!rects.length) return null;
  let x = 0;
  let y = 0;
  let w = 0;
  let h = 0;
  for (const r of rects) {
    x += r.x;
    y += r.y;
    w += r.w;
    h += r.h;
  }
  const n = rects.length;
  return { x: x / n, y: y / n, w: w / n, h: h / n };
}

function fitPortraitCrop(bodyRoi) {
  if (!bodyRoi) {
    return { x: 0.25, y: 0, w: 0.5, h: 1 };
  }
  let cx = bodyRoi.x + bodyRoi.w / 2;
  let cy = bodyRoi.y + bodyRoi.h / 2;
  let cropH = Math.min(1, Math.max(bodyRoi.h * 1.18, bodyRoi.w / ASPECT));
  let cropW = cropH * ASPECT;
  if (cropW > 1) {
    cropW = 1;
    cropH = cropW / ASPECT;
  }
  if (cropH > 1) {
    cropH = 1;
    cropW = cropH * ASPECT;
  }
  // Same zoom; shift crop right so the player sits a bit left and the table
  // / sparring stay visible on the right.
  const playerXInCrop = 0.32;
  let x = cx - cropW * playerXInCrop;
  let y = cy - cropH / 2;
  x = clamp01(Math.min(x, 1 - cropW));
  y = clamp01(Math.min(y, 1 - cropH));
  if (bodyRoi) {
    const margin = 0.012;
    const leftPad = cropW * 0.03;
    const left = clamp01(bodyRoi.x - margin);
    const right = clamp01(bodyRoi.x + bodyRoi.w + margin);
    const minX = Math.max(0, right - cropW);
    const maxX = Math.min(1 - cropW, Math.max(minX, left - leftPad));
    x = Math.min(maxX, Math.max(minX, x));
  }
  return { x, y, w: cropW, h: cropH };
}

export function computePortraitCropRect(samples, poseSide, sourceStartSec, sourceEndSec) {
  const rects = [];
  const startMs = Math.round(sourceStartSec * 1000);
  const endMs = Math.round(sourceEndSec * 1000);
  for (const sample of samples || []) {
    const tMs = Number(sample?.t_ms);
    if (!Number.isFinite(tMs) || tMs < startMs || tMs > endMs) continue;
    const side = poseSide === "right" ? sample.right : sample.left;
    const roi =
      side?.roi || side?.roi_box || roiFromJoints(side?.joints_full || side?.joints);
    if (roi) rects.push(roi);
  }
  const avg = averageCropRect(rects);
  const padded = avg ? padNormalizedRoi(avg, 0.07) : null;
  return fitPortraitCrop(padded);
}


function even(n) {
  const v = Math.floor(Number(n) || 0);
  return v % 2 === 0 ? v : v - 1;
}

function isSafari() {
  if (typeof navigator === "undefined") return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent || "");
}

/** Canvas captureStream works with WebM in Chrome; Safari needs MP4. */
export function pickCanvasRecorderMimeType(isTypeSupported) {
  const probe =
    typeof isTypeSupported === "function"
      ? isTypeSupported
      : typeof MediaRecorder !== "undefined" &&
          typeof MediaRecorder.isTypeSupported === "function"
        ? (type) => MediaRecorder.isTypeSupported(type)
        : null;
  const candidates = isSafari()
    ? ["video/mp4;codecs=avc1.42E01E", "video/mp4", "video/webm"]
    : [
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
        "video/mp4;codecs=avc1.42E01E",
        "video/mp4",
      ];
  if (!probe) return candidates[0];
  for (const type of candidates) {
    try {
      if (probe(type)) return type;
    } catch {
      /* ignore */
    }
  }
  return "";
}

function readBrandConfig() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem("brand_config") || "null");
  } catch {
    return null;
  }
}

function logoFetchHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token ? { Authorization: token } : {};
}

function resolveCurrentClubLogoUrl() {
  if (typeof window === "undefined") return null;
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const group = JSON.parse(localStorage.getItem("grpinf") || "{}");
    const brand = readBrandConfig();
    const clubImage =
      user?.club_details?.club_image ||
      user?.club?.club_image ||
      user?.club_image ||
      group?.groupDetails?.club_image ||
      group?.groupDetails?.club?.club_image ||
      null;
    // Match header / iOS: brand logo first, then club image, then favicon.
    const raw =
      brand?.logo_url ||
      clubImage ||
      brand?.favicon_url ||
      null;
    return resolveClubImageUrl(raw) || resolveClubLogoUrl(clubImage, brand);
  } catch {
    return null;
  }
}

function decodeImageElement(src, crossOrigin = false) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function loadCanvasImage(src) {
  if (!src) return null;
  let objectUrl = null;
  try {
    if (/^https?:\/\//i.test(src)) {
      // S3 club logos have no CORS — proxy through API (same as evaluation video).
      objectUrl = await fetchProxiedRemoteBlobUrl(src);
      if (objectUrl) {
        const img = await decodeImageElement(objectUrl);
        return { img, objectUrl };
      }
      try {
        const resp = await fetch(src, {
          mode: "cors",
          headers: logoFetchHeaders(),
        });
        if (resp.ok) {
          const blob = await resp.blob();
          if (blob?.size) {
            objectUrl = URL.createObjectURL(blob);
            const img = await decodeImageElement(objectUrl);
            return { img, objectUrl };
          }
        }
      } catch {
        /* fall through to crossOrigin image load */
      }
      try {
        const img = await decodeImageElement(src, true);
        return { img, objectUrl: null };
      } catch {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        return null;
      }
    }
    const img = await decodeImageElement(src);
    return { img, objectUrl: null };
  } catch {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    return null;
  }
}

async function loadExportLogos() {
  const [clubLogo, smartttLogo] = await Promise.all([
    loadCanvasImage(resolveCurrentClubLogoUrl()),
    loadCanvasImage(defaultAppLogo),
  ]);
  return { clubLogo, smartttLogo };
}

async function presentSourceFrame(video, sourceSec, sourceFps = OUTPUT_FPS) {
  const target = Number(sourceSec) || 0;
  const cur = Number(video.currentTime) || 0;
  const frameDt = 1 / Math.max(30, Number(sourceFps) || OUTPUT_FPS);
  const epsilon = Math.min(0.004, frameDt * 0.35);
  if (Math.abs(cur - target) < epsilon) return;
  await seekVideo(video, target, epsilon);
}

function seekVideo(video, timeSec, epsilon = 0.004) {
  return new Promise((resolve) => {
    const duration = Number(video.duration);
    const target = Math.max(
      0,
      Math.min(timeSec, Number.isFinite(duration) ? duration : timeSec)
    );
    const eps = Number(epsilon) > 0 ? Number(epsilon) : 0.004;
    if (Math.abs((video.currentTime || 0) - target) < eps) {
      resolve();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      video.removeEventListener("seeked", finish);
      resolve();
    };
    video.addEventListener("seeked", finish);
    try {
      video.currentTime = target;
    } catch {
      finish();
      return;
    }
    window.setTimeout(finish, 800);
  });
}

function drawLetterboxedFrame(ctx, video, cropRect) {
  const { videoWidth: vw, videoHeight: vh } = video;
  if (!(vw > 0 && vh > 0)) return;
  const sx = (cropRect?.x || 0) * vw;
  const sy = (cropRect?.y || 0) * vh;
  const sw = (cropRect?.w || 1) * vw;
  const sh = (cropRect?.h || 1) * vh;
  const scale = Math.min(ctx.canvas.width / sw, ctx.canvas.height / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = (ctx.canvas.width - dw) / 2;
  const dy = (ctx.canvas.height - dh) / 2;

  ctx.save();
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  // Fill the padding with a blurred extension of the cropped image so the bars
  // match the clip colors without stretching the sharp foreground.
  ctx.filter = "blur(26px)";
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.filter = "none";
  ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
  ctx.restore();
  return { x: dx, y: dy, w: dw, h: dh };
}

function isNearWhite(r, g, b, threshold = 242) {
  return r > threshold && g > threshold && b > threshold;
}

/** Remove only outer white matte; keep enclosed white inside the shield graphic. */
function drawImageWithOuterWhiteKey(ctx, img, x, y, w, h) {
  const scratch = document.createElement("canvas");
  scratch.width = Math.max(1, w);
  scratch.height = Math.max(1, h);
  const sctx = scratch.getContext("2d");
  if (!sctx) {
    ctx.drawImage(img, x, y, w, h);
    return;
  }
  sctx.drawImage(img, 0, 0, w, h);
  const data = sctx.getImageData(0, 0, w, h);
  const px = data.data;
  const width = w;
  const height = h;
  const visited = new Uint8Array(width * height);
  const queue = [];

  const idx = (col, row) => row * width + col;
  const pixelAt = (col, row) => {
    const i = idx(col, row) * 4;
    return [px[i], px[i + 1], px[i + 2]];
  };
  const seed = (col, row) => {
    const id = idx(col, row);
    if (visited[id]) return;
    const [r, g, b] = pixelAt(col, row);
    if (!isNearWhite(r, g, b)) return;
    visited[id] = 1;
    queue.push([col, row]);
  };

  for (let col = 0; col < width; col += 1) {
    seed(col, 0);
    seed(col, height - 1);
  }
  for (let row = 1; row < height - 1; row += 1) {
    seed(0, row);
    seed(width - 1, row);
  }

  while (queue.length) {
    const [col, row] = queue.pop();
    for (const [dc, dr] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nc = col + dc;
      const nr = row + dr;
      if (nc < 0 || nr < 0 || nc >= width || nr >= height) continue;
      const id = idx(nc, nr);
      if (visited[id]) continue;
      const [r, g, b] = pixelAt(nc, nr);
      if (!isNearWhite(r, g, b)) continue;
      visited[id] = 1;
      queue.push([nc, nr]);
    }
  }

  for (let i = 0; i < visited.length; i += 1) {
    if (visited[i]) px[i * 4 + 3] = 0;
  }

  sctx.putImageData(data, 0, 0);
  ctx.drawImage(scratch, x, y);
}

function drawCornerLogo(
  ctx,
  asset,
  corner = "top-left",
  { background = true, whiteKey = false } = {}
) {
  const img = asset?.img;
  if (!img?.naturalWidth || !img?.naturalHeight) return;
  const pad = Math.round(ctx.canvas.width * 0.032);
  const maxW = ctx.canvas.width * 0.22;
  const maxH = ctx.canvas.height * 0.1;
  const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
  const w = Math.max(44, Math.round(img.naturalWidth * scale));
  const h = Math.max(24, Math.round(img.naturalHeight * scale));
  const x = corner === "top-left" ? pad : ctx.canvas.width - pad - w;
  const y = corner === "top-left" ? pad : ctx.canvas.height - pad - h;

  ctx.save();
  if (background) {
    ctx.fillStyle = "rgba(0, 0, 0, 0.28)";
    ctx.fillRect(x - 8, y - 6, w + 16, h + 12);
  } else {
    ctx.shadowColor = "rgba(0, 0, 0, 0.65)";
    ctx.shadowBlur = Math.max(6, Math.round(ctx.canvas.width * 0.012));
    ctx.shadowOffsetY = 2;
  }
  if (whiteKey) drawImageWithOuterWhiteKey(ctx, img, x, y, w, h);
  else ctx.drawImage(img, x, y, w, h);
  ctx.restore();
}

function drawStrokesPerMinuteBadge(ctx, strokesPerMinute) {
  const spm = Number(strokesPerMinute);
  if (!Number.isFinite(spm) || spm < 0) return;
  const value = Math.round(spm);
  const label = String(value);
  const unit = "/ min";
  const pad = Math.round(ctx.canvas.width * 0.032);
  const valueSize = Math.max(28, Math.round(ctx.canvas.width * 0.078));
  const unitSize = Math.max(12, Math.round(valueSize * 0.38));
  ctx.save();
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.font = `800 ${valueSize}px "Bebas Neue", Impact, sans-serif`;
  const valueWidth = ctx.measureText(label).width;
  ctx.font = `600 ${unitSize}px Montserrat, sans-serif`;
  const unitWidth = ctx.measureText(unit).width;
  const blockW = Math.max(valueWidth, unitWidth);
  const tx = ctx.canvas.width - pad;
  const ty = pad;
  const bgPadX = 12;
  const bgPadY = 8;
  const blockH = valueSize + unitSize + 4;
  ctx.fillStyle = "rgba(4, 8, 28, 0.72)";
  roundRectPath(
    ctx,
    tx - blockW - bgPadX,
    ty - bgPadY / 2,
    blockW + bgPadX * 2,
    blockH + bgPadY,
    10
  );
  ctx.fill();
  ctx.font = `800 ${valueSize}px "Bebas Neue", Impact, sans-serif`;
  ctx.lineWidth = Math.max(4, Math.round(valueSize * 0.12));
  ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
  ctx.fillStyle = "#FFFFFF";
  ctx.strokeText(label, tx, ty);
  ctx.fillText(label, tx, ty);
  ctx.font = `600 ${unitSize}px Montserrat, sans-serif`;
  ctx.fillStyle = "rgba(255, 255, 255, 0.82)";
  ctx.fillText(unit, tx, ty + valueSize + 2);
  ctx.restore();
}

function roundRectPath(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function fitFontSize(ctx, text, family, weight, maxWidth, maxSize, minSize) {
  let size = maxSize;
  while (size > minSize) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) return size;
    size -= 2;
  }
  return minSize;
}

function drawHeartShape(ctx, cx, cy, size) {
  const s = Math.max(10, size);
  ctx.beginPath();
  ctx.moveTo(cx, cy + s * 0.32);
  ctx.bezierCurveTo(
    cx - s * 0.95,
    cy - s * 0.28,
    cx - s * 0.55,
    cy - s * 0.92,
    cx,
    cy - s * 0.38
  );
  ctx.bezierCurveTo(
    cx + s * 0.55,
    cy - s * 0.92,
    cx + s * 0.95,
    cy - s * 0.28,
    cx,
    cy + s * 0.32
  );
  ctx.closePath();
}

function heartBumpScale(sourceSec, bpm) {
  const rate = Math.max(40, Math.min(220, Number(bpm) || 0));
  if (!(rate > 0)) return 1;
  const beat = ((Number(sourceSec) || 0) * rate) / 60;
  const phase = beat - Math.floor(beat);
  if (phase < 0.14) return 1 + 0.34 * Math.sin((phase / 0.14) * Math.PI);
  if (phase < 0.26) return 1 + 0.18 * Math.sin(((phase - 0.14) / 0.12) * Math.PI);
  return 1;
}

function drawBumpingHeartRate(ctx, bpm, sourceSec) {
  const value = Number(bpm);
  if (!Number.isFinite(value) || value <= 0) return;
  const w = ctx.canvas.width;
  const pad = Math.round(w * 0.032);
  const fontSize = Math.max(18, Math.round(w * 0.046));
  const label = `${Math.round(value)}`;
  const unit = "bpm";
  ctx.save();
  ctx.font = `800 ${fontSize}px Montserrat, sans-serif`;
  const labelWidth = ctx.measureText(label).width;
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  const tx = w - pad;
  const ty = pad;
  ctx.lineWidth = 5;
  ctx.strokeStyle = "rgba(8, 12, 32, 0.85)";
  ctx.fillStyle = "#FFFFFF";
  ctx.strokeText(label, tx, ty);
  ctx.fillText(label, tx, ty);
  ctx.font = `700 ${Math.max(11, Math.round(fontSize * 0.42))}px Montserrat, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.86)";
  ctx.strokeText(unit, tx, ty + fontSize + 2);
  ctx.fillText(unit, tx, ty + fontSize + 2);

  const heartSize = Math.max(13, Math.round(fontSize * 0.72));
  const scale = heartBumpScale(sourceSec, value);
  const hx = tx - labelWidth - heartSize - 10;
  const hy = ty + fontSize * 0.42;
  ctx.translate(hx, hy);
  ctx.scale(scale, scale);
  drawHeartShape(ctx, 0, 0, heartSize);
  ctx.fillStyle = "#FF2D6A";
  ctx.shadowColor = "rgba(255, 45, 106, 0.7)";
  ctx.shadowBlur = 12;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "#FFFFFF";
  ctx.stroke();
  ctx.restore();
}

function drawSocialReelOverlay(ctx, overlay) {
  const title = String(overlay?.title || "").trim();
  const subtitle = String(overlay?.subtitle || "").trim();
  const badge = String(overlay?.badge || "").trim();
  const performerName = String(overlay?.performerName || "").trim();
  const performerLevel = String(overlay?.performerLevel || "").trim();
  const partnerName = String(overlay?.partnerName || "").trim();
  const partnerRole = String(overlay?.partnerRole || "").trim();
  const strokesPerMinute = Number(overlay?.strokesPerMinute);
  const owners = Array.isArray(overlay?.owners) ? overlay.owners : [];
  if (
    !title &&
    !subtitle &&
    !badge &&
    !performerName &&
    !partnerName &&
    !owners.length &&
    !(Number.isFinite(strokesPerMinute) && strokesPerMinute >= 0)
  ) {
    return;
  }

  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const pad = Math.round(w * 0.04);
  const logoReserve = Math.round(h * 0.14);
  const titleTop = pad + logoReserve;

  ctx.save();
  const topFade = ctx.createLinearGradient(0, 0, 0, Math.round(h * 0.34));
  topFade.addColorStop(0, "rgba(4, 8, 28, 0.62)");
  topFade.addColorStop(0.6, "rgba(4, 8, 28, 0.18)");
  topFade.addColorStop(1, "rgba(4, 8, 28, 0)");
  ctx.fillStyle = topFade;
  ctx.fillRect(0, 0, w, Math.round(h * 0.34));

  const bottomBand = Math.round(h * 0.28);
  const bottomFade = ctx.createLinearGradient(0, h - bottomBand, 0, h);
  bottomFade.addColorStop(0, "rgba(4, 8, 28, 0)");
  bottomFade.addColorStop(0.45, "rgba(4, 8, 28, 0.28)");
  bottomFade.addColorStop(1, "rgba(4, 8, 28, 0.72)");
  ctx.fillStyle = bottomFade;
  ctx.fillRect(0, h - bottomBand, w, bottomBand);

  let stackTop = titleTop;
  if (title) {
    const titleSize = fitFontSize(
      ctx,
      title,
      '"Bebas Neue", Impact, sans-serif',
      "400",
      w - pad * 2,
      Math.round(w * 0.11),
      Math.round(w * 0.055)
    );
    ctx.font = `400 ${titleSize}px "Bebas Neue", Impact, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.lineJoin = "round";
    ctx.miterLimit = 2;
    ctx.shadowColor = "rgba(255, 61, 148, 0.55)";
    ctx.shadowBlur = 18;
    ctx.lineWidth = Math.max(6, Math.round(titleSize * 0.12));
    ctx.strokeStyle = "#12081C";
    ctx.strokeText(title, w / 2, titleTop);
    const titleGrad = ctx.createLinearGradient(
      w * 0.2,
      titleTop,
      w * 0.8,
      titleTop + titleSize
    );
    titleGrad.addColorStop(0, "#FFE566");
    titleGrad.addColorStop(0.55, "#FFFFFF");
    titleGrad.addColorStop(1, "#7CFFFB");
    ctx.fillStyle = titleGrad;
    ctx.fillText(title, w / 2, titleTop);
    ctx.shadowBlur = 0;
    stackTop = titleTop + titleSize + Math.round(w * 0.018);
  }

  // Performer first — large, high-contrast name under the stroke title.
  if (performerName) {
    const perfLine = performerLevel
      ? `${performerName} · ${performerLevel}`
      : performerName;
    const perfSize = fitFontSize(
      ctx,
      perfLine,
      '"Bebas Neue", Impact, "Arial Black", sans-serif',
      "400",
      w - pad * 2,
      Math.round(w * 0.078),
      Math.round(w * 0.048)
    );
    ctx.font = `400 ${perfSize}px "Bebas Neue", Impact, "Arial Black", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(5, Math.round(perfSize * 0.14));
    ctx.strokeStyle = "rgba(4, 8, 28, 0.92)";
    ctx.strokeText(perfLine, w / 2, stackTop);
    ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
    ctx.shadowBlur = 10;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(perfLine, w / 2, stackTop);
    ctx.shadowBlur = 0;
    stackTop += perfSize + Math.round(w * 0.01);
  }

  // Sparring — secondary, quieter line under the performer (always show name).
  if (partnerName) {
    const roleBit = String(partnerRole || "Sparring").trim() || "Sparring";
    const partnerLabel = `${roleBit} · ${partnerName}`;
    const partnerSize = fitFontSize(
      ctx,
      partnerLabel,
      "Montserrat, system-ui, sans-serif",
      "500",
      w - pad * 2,
      Math.round(w * 0.028),
      Math.round(w * 0.02)
    );
    ctx.font = `500 ${partnerSize}px Montserrat, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(255, 255, 255, 0.42)";
    ctx.fillText(partnerLabel, w / 2, stackTop);
    stackTop += partnerSize + Math.round(w * 0.012);
  }

  const bottomPad = Math.round(w * 0.028);
  const ownerLine = Math.max(13, Math.round(w * 0.032));
  const logoClear = Math.round(h * 0.09);
  let metricsBottom = h - bottomPad - logoClear;

  if (subtitle) {
    const subSize = fitFontSize(
      ctx,
      subtitle,
      "Montserrat, sans-serif",
      "600",
      w - pad * 2,
      Math.round(w * 0.034),
      Math.round(w * 0.024)
    );
    ctx.font = `600 ${subSize}px Montserrat, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(8, 12, 32, 0.75)";
    ctx.strokeText(subtitle, w / 2, metricsBottom);
    ctx.fillStyle = "rgba(255, 255, 255, 0.72)";
    ctx.fillText(subtitle, w / 2, metricsBottom);
    metricsBottom -= subSize + Math.round(w * 0.012);
  }

  if (badge) {
    const badgeSize = Math.max(16, Math.round(w * 0.038));
    ctx.font = `800 ${badgeSize}px Montserrat, sans-serif`;
    const textW = ctx.measureText(badge).width;
    const bw = textW + Math.round(w * 0.07);
    const bh = badgeSize + Math.round(w * 0.028);
    const bx = (w - bw) / 2;
    const by = metricsBottom - bh;
    const pill = ctx.createLinearGradient(bx, by, bx + bw, by + bh);
    pill.addColorStop(0, "#FF3D94");
    pill.addColorStop(1, "#FF8A1A");
    roundRectPath(ctx, bx, by, bw, bh, bh / 2);
    ctx.fillStyle = pill;
    ctx.shadowColor = "rgba(255, 61, 148, 0.45)";
    ctx.shadowBlur = 16;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
    ctx.stroke();
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(badge, w / 2, by + bh / 2 + 1);
    metricsBottom = by - Math.round(w * 0.016);
  }

  if (!performerName && owners.length) {
    const lineSize = ownerLine;
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    let oy = h - bottomPad;
    for (let i = owners.length - 1; i >= 0; i -= 1) {
      const owner = owners[i] || {};
      const parts = [owner.name, owner.role, owner.level].filter(
        (part) => part && String(part).trim()
      );
      const line = parts.join(" · ");
      if (!line) continue;
      const size = fitFontSize(
        ctx,
        line,
        "Montserrat, sans-serif",
        "700",
        w * 0.52,
        lineSize,
        11
      );
      ctx.font = `700 ${size}px Montserrat, sans-serif`;
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(8, 12, 32, 0.8)";
      ctx.fillStyle = "#FFFFFF";
      ctx.strokeText(line, pad, oy);
      ctx.fillText(line, pad, oy);
      oy -= size + 6;
    }
  }

  if (Number.isFinite(strokesPerMinute) && strokesPerMinute >= 0) {
    drawStrokesPerMinuteBadge(ctx, strokesPerMinute);
  }
  ctx.restore();
}

async function ensureReelFonts() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("smarttt-reel-fonts")) {
    const link = document.createElement("link");
    link.id = "smarttt-reel-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Montserrat:wght@500;600;700;800&display=swap";
    document.head.appendChild(link);
  }
  if (!document.fonts?.load) return;
  try {
    await Promise.all([
      document.fonts.load('72px "Bebas Neue"'),
      document.fonts.load('56px "Bebas Neue"'),
      document.fonts.load("500 18px Montserrat"),
      document.fonts.load("800 28px Montserrat"),
    ]);
  } catch {
    /* system fallbacks still paint */
  }
}

function paintFrame({
  ctx,
  video,
  cropRect,
  poseSamples,
  poseSide,
  sourceSec,
  kneeMinState,
  logos,
  strokesPerMinute,
  socialOverlay,
  hrPoints,
  hrDurationSec,
  showKnees = false,
}) {
  const videoRect = drawLetterboxedFrame(ctx, video, cropRect);
  if (showKnees) {
    const knee = kneeStateAtTime(poseSamples, poseSide, sourceSec, kneeMinState);
    drawKneeOverlayOnCanvas(
      ctx,
      knee.sample,
      {
        left: knee.left,
        right: knee.right,
        minLeft: knee.minLeft,
        minRight: knee.minRight,
      },
      cropRect,
      videoRect
    );
  }
  if (logos?.clubLogo) drawCornerLogo(ctx, logos.clubLogo, "top-left");
  if (socialOverlay) drawSocialReelOverlay(ctx, socialOverlay);
  else drawStrokesPerMinuteBadge(ctx, strokesPerMinute);
  const bpm = hrAtVideoTime(hrPoints, sourceSec, {
    videoDurationSec: hrDurationSec,
  });
  if (bpm != null) drawBumpingHeartRate(ctx, bpm, Date.now() / 1000);
  if (logos?.smartttLogo) {
    drawCornerLogo(ctx, logos.smartttLogo, "bottom-right", {
      background: false,
      whiteKey: true,
    });
  }
}

function waitMs(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function attachHiddenVideo(sourceVideo, signal, { withAudio = false } = {}) {
  const clone = document.createElement("video");
  clone.muted = !withAudio;
  clone.defaultMuted = !withAudio;
  clone.playsInline = true;
  clone.setAttribute("playsinline", "true");
  clone.preload = "auto";
  clone.disableRemotePlayback = true;
  const rawSrc = String(sourceVideo.currentSrc || sourceVideo.src || "").trim();
  let src = rawSrc;
  if (rawSrc && !rawSrc.startsWith("blob:") && !rawSrc.startsWith("data:")) {
    src = await ensurePoseVideoBlobUrl(rawSrc, signal);
  }
  if (!src) throw new Error("Could not load source video for export");
  // Same-origin blob: avoids canvas taint; do not set CORS on blob URLs.
  if (!src.startsWith("blob:") && !src.startsWith("data:")) {
    clone.crossOrigin = "anonymous";
  }
  clone.src = src;
  const srcW = Math.max(1, Number(sourceVideo.videoWidth) || 640);
  const srcH = Math.max(1, Number(sourceVideo.videoHeight) || 360);
  // Keep a real in-viewport box. Tiny offscreen videos get decode-throttled
  // (often ~30fps), which would drop unique 120fps frames.
  const scale = Math.min(1, 720 / Math.max(srcW, srcH));
  const cssW = Math.max(240, Math.round(srcW * scale));
  const cssH = Math.max(135, Math.round(srcH * scale));
  clone.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    `width:${cssW}px`,
    `height:${cssH}px`,
    "opacity:0.02",
    "pointer-events:none",
    "z-index:0",
  ].join(";");
  document.body.appendChild(clone);
  try {
    await new Promise((resolve, reject) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      clone.addEventListener("loadeddata", done, { once: true });
      clone.addEventListener(
        "error",
        () => {
          if (settled) return;
          settled = true;
          reject(new Error("Could not load source video for export"));
        },
        { once: true }
      );
      try {
        clone.load();
      } catch {
        /* ignore */
      }
      window.setTimeout(done, 8000);
    });
    return clone;
  } catch (err) {
    clone.remove();
    throw err;
  }
}

async function resolveExportSourceFps(video, hintFps) {
  const hinted = snapSourceFps(hintFps);
  if (hinted) return hinted;
  try {
    video.playbackRate = 1;
    await video.play();
    const t0 =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const f0 = video.getVideoPlaybackQuality?.()?.totalVideoFrames || 0;
    await waitMs(280);
    const dt =
      ((typeof performance !== "undefined" ? performance.now() : Date.now()) -
        t0) /
      1000;
    const f1 = video.getVideoPlaybackQuality?.()?.totalVideoFrames || 0;
    try {
      video.pause();
    } catch {
      /* ignore */
    }
    if (dt > 0.08 && f1 > f0 + 2) {
      const measured = snapSourceFps((f1 - f0) / dt);
      if (measured) return measured;
    }
  } catch {
    try {
      video.pause();
    } catch {
      /* ignore */
    }
  }
  return 60;
}

function captureCanvasStream(canvas, fps = OUTPUT_FPS) {
  if (!canvas || typeof canvas.captureStream !== "function") {
    throw new Error("Canvas capture is not supported in this browser");
  }
  const rate = Number(fps) > 0 ? Number(fps) : OUTPUT_FPS;
  try {
    const stream = canvas.captureStream(rate);
    const track = stream.getVideoTracks?.()[0];
    if (track) {
      try {
        track.contentHint = "motion";
      } catch {
        /* ignore */
      }
    }
    return stream;
  } catch (first) {
    try {
      return canvas.captureStream();
    } catch (second) {
      const msg = String(second?.message || first?.message || "");
      if (/taint|captureStream|SecurityError/i.test(msg) || first?.name === "SecurityError") {
        throw new Error(
          "Could not record this clip (cross-origin video). Retry after the clip finishes loading."
        );
      }
      throw second || first;
    }
  }
}

function attachHiddenCanvas(canvas) {
  canvas.style.cssText =
    "position:fixed;left:-9999px;top:0;width:9px;height:16px;opacity:0;pointer-events:none;";
  document.body.appendChild(canvas);
}

async function waitUntil(targetMs) {
  const delay = targetMs - (typeof performance !== "undefined" ? performance.now() : Date.now());
  if (delay > 1) await waitMs(delay);
}

async function playSegment({
  video,
  ctx,
  cropRect,
  poseSamples,
  poseSide,
  kneeMinState,
  logos,
  strokesPerMinute,
  socialOverlay,
  hrPoints,
  hrDurationSec,
  segment,
  sourceFps,
  recordFps = OUTPUT_FPS,
  signal,
  onTick,
}) {
  const showKnees = Boolean(segment.showKnees);
  const freezeSec = Math.max(0, Number(segment.freezeSec) || 0);
  const span = Math.max(0, Number(segment.sourceEnd) - Number(segment.sourceStart));
  const outFps = Number(recordFps) > 0 ? Number(recordFps) : OUTPUT_FPS;
  const paint = (sourceSec) => {
    paintFrame({
      ctx,
      video,
      cropRect,
      poseSamples,
      poseSide,
      sourceSec,
      kneeMinState,
      logos,
      strokesPerMinute,
      socialOverlay,
      hrPoints,
      hrDurationSec,
      showKnees,
    });
    onTick?.(sourceSec);
  };

  if (freezeSec > 0.05 && span <= 0.02) {
    await seekVideo(video, segment.sourceStart);
    try {
      video.pause();
      video.muted = true;
    } catch {
      /* ignore */
    }
    const frames = Math.max(1, Math.round(freezeSec * outFps));
    const t0 =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    for (let i = 0; i < frames; i += 1) {
      if (signal?.aborted) return;
      paint(segment.sourceStart);
      await waitUntil(t0 + ((i + 1) * 1000) / outFps);
    }
    return;
  }

  if (span <= 0.02) return;
  const rate = 1 / Math.max(1, Number(segment.timeScale) || 1);
  const grid = sourceSamplePeriodSec(sourceFps, rate, outFps);
  await seekVideo(video, segment.sourceStart);
  video.playbackRate = Math.max(0.08, Math.min(1, rate));
  video.muted = !segmentReelAudioEnabled(segment);
  if (!video.muted) {
    video.volume = 0.001;
  }
  try {
    await video.play();
  } catch {
    /* autoplay may still paint via currentTime */
  }

  await new Promise((resolve) => {
    let lastBucket = sourceFrameBucket(segment.sourceStart, grid) - 1;
    let settled = false;
    let rvfcId = null;
    let pollTimer = 0;
    let gotRvfc = false;
    let limitTimer = 0;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (rvfcId != null && typeof video.cancelVideoFrameCallback === "function") {
        try {
          video.cancelVideoFrameCallback(rvfcId);
        } catch {
          /* ignore */
        }
      }
      if (pollTimer) window.clearTimeout(pollTimer);
      if (limitTimer) window.clearTimeout(limitTimer);
      try {
        video.pause();
      } catch {
        /* ignore */
      }
      resolve();
    };
    limitTimer = window.setTimeout(finish, (span / rate + 1.5) * 1000);

    const onPresented = (mediaTime, fromRvfc = false) => {
      if (signal?.aborted || settled) {
        finish();
        return;
      }
      if (fromRvfc) gotRvfc = true;
      const t = Number(mediaTime);
      const srcT = Number.isFinite(t)
        ? t
        : Number(video.currentTime) || segment.sourceStart;
      const bucket = sourceFrameBucket(srcT, grid);
      if (bucket > lastBucket) {
        lastBucket = bucket;
        paint(srcT);
      }
      if (srcT >= segment.sourceEnd - 0.02 || video.ended) {
        finish();
        return;
      }
      if (fromRvfc) scheduleRvfc();
    };

    const scheduleRvfc = () => {
      if (settled) return;
      if (typeof video.requestVideoFrameCallback !== "function") return;
      rvfcId = video.requestVideoFrameCallback((_now, meta) => {
        onPresented(meta?.mediaTime, true);
      });
    };

    const poll = () => {
      if (settled) return;
      if (!gotRvfc) onPresented(video.currentTime, false);
      pollTimer = window.setTimeout(poll, 1000 / outFps);
    };

    scheduleRvfc();
    pollTimer = window.setTimeout(poll, 80);
  });
}

/**
 * Export a portrait slow-mo reel with knee overlays.
 * Plays a hidden copy of the source (so the review player does not flicker)
 * and records the canvas in real time — Chrome canvas+mp4 often yields an empty blob.
 */
export async function exportStrokeSlowMoReel({
  video,
  poseSamples,
  segments,
  poseSide = "left",
  strokesPerMinute = null,
  socialOverlay = null,
  hrPoints = null,
  sourceFps = null,
  onProgress,
  signal,
}) {
  if (!video || !segments?.length) {
    throw new Error("Missing video or clip segments");
  }
  if (typeof document === "undefined") {
    throw new Error("Video export is not supported in this browser");
  }
  if (
    !canUseWebCodecsReelEncoder() &&
    typeof MediaRecorder === "undefined"
  ) {
    throw new Error("Video export is not supported in this browser");
  }

  const sourceStart = segments[0].sourceStart;
  const sourceEnd = segments[segments.length - 1].sourceEnd;
  const hrDurationSec =
    Number(video?.duration) ||
    (Number.isFinite(sourceEnd) ? sourceEnd : 0);
  const cropRect = computePortraitCropRect(
    poseSamples,
    poseSide,
    sourceStart,
    sourceEnd
  );
  const kneeMinState = buildKneeOverlayState(
    poseSamples,
    poseSide,
    sourceStart,
    sourceEnd
  );

  const totalOutSec = outputDurationSeconds(segments) || 1;

  const canvas = document.createElement("canvas");
  canvas.width = even(OUTPUT_WIDTH);
  canvas.height = even(OUTPUT_HEIGHT);
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
  if (!ctx) throw new Error("Canvas not supported");
  attachHiddenCanvas(canvas);

  let hidden = null;
  let recorder = null;
  const chunks = [];
  const logos = await loadExportLogos();
  await ensureReelFonts();

  try {
    hidden = await attachHiddenVideo(video, signal, {
      withAudio: reelExportNeedsAudio(segments),
    });
    const exportFps = await resolveExportSourceFps(hidden, sourceFps);
    const recordFps = resolveReelOutputFps(exportFps);
    const recordBitrate = reelBitrateForFps(recordFps);
    try {
      hidden.pause();
    } catch {
      /* ignore */
    }
    await seekVideo(hidden, sourceStart);

    const paintAt = (sourceSec, showKnees) =>
      paintFrame({
        ctx,
        video: hidden,
        cropRect,
        poseSamples,
        poseSide,
        sourceSec,
        kneeMinState,
        logos,
        strokesPerMinute,
        socialOverlay,
        hrPoints,
        hrDurationSec,
        showKnees,
      });

    paintAt(sourceStart, Boolean(segments[0]?.showKnees));

    const timeline = buildOutputTimeline(segments, recordFps);
    if (canUseWebCodecsReelEncoder() && timeline.length) {
      try {
        const blob = await encodeCanvasFramesToMp4({
          canvas,
          frameCount: timeline.length,
          fps: recordFps,
          bitrate: Math.min(recordBitrate, 16_000_000),
          signal,
          segments,
          sourceVideo: hidden,
          paintFrame: async (index) => {
            const step = timeline[index];
            await presentSourceFrame(hidden, step.sourceSec, exportFps);
            paintAt(step.sourceSec, step.showKnees);
          },
          onProgress,
        });
        if (blob?.size) return blob;
      } catch (err) {
        if (signal?.aborted) throw err;
        /* fall back to realtime canvas capture */
      }
    }

    if (typeof MediaRecorder === "undefined") {
      throw new Error("Video export is not supported in this browser");
    }

    const mimeType = pickCanvasRecorderMimeType();
    const canvasStream = captureCanvasStream(canvas, recordFps);
    let stream = canvasStream;
    if (reelExportNeedsAudio(segments) && typeof hidden.captureStream === "function") {
      try {
        hidden.muted = false;
        hidden.volume = 0.001;
        const mediaStream = hidden.captureStream();
        const audioTracks = mediaStream.getAudioTracks();
        if (audioTracks.length) {
          stream = new MediaStream([
            ...canvasStream.getVideoTracks(),
            ...audioTracks,
          ]);
        }
      } catch {
        stream = canvasStream;
      }
    }

    try {
      recorder = mimeType
        ? new MediaRecorder(stream, {
            mimeType,
            videoBitsPerSecond: recordBitrate,
          })
        : new MediaRecorder(stream, { videoBitsPerSecond: recordBitrate });
    } catch {
      recorder = new MediaRecorder(stream);
    }

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data);
    };

    await new Promise((resolve, reject) => {
      const fail = () => reject(new Error("Recording failed to start"));
      recorder.addEventListener("start", resolve, { once: true });
      recorder.addEventListener("error", fail, { once: true });
      try {
        recorder.start(500);
      } catch (err) {
        reject(err);
      }
      window.setTimeout(resolve, 400);
    });

    const startedAt = Date.now();
    for (const segment of segments) {
      if (signal?.aborted) throw new Error("Export cancelled");
      await playSegment({
        video: hidden,
        ctx,
        cropRect,
        poseSamples,
        poseSide,
        kneeMinState,
        logos,
        strokesPerMinute,
        socialOverlay,
        hrPoints,
        hrDurationSec,
        segment,
        sourceFps: exportFps,
        recordFps,
        signal,
        onTick: () => {
          const elapsed = (Date.now() - startedAt) / 1000;
          onProgress?.({
            fraction: Math.min(0.99, elapsed / totalOutSec),
            frame: 0,
            totalFrames: 0,
          });
        },
      });
    }

    paintFrame({
      ctx,
      video: hidden,
      cropRect,
      poseSamples,
      poseSide,
      sourceSec: sourceEnd,
      kneeMinState,
      logos,
      strokesPerMinute,
      socialOverlay,
      hrPoints,
      hrDurationSec,
      showKnees: Boolean(segments[segments.length - 1]?.showKnees),
    });
    await waitMs(250);
    if (typeof recorder.requestData === "function") {
      try {
        recorder.requestData();
      } catch {
        /* ignore */
      }
    }
    await waitMs(200);

    await new Promise((resolve) => {
      recorder.onstop = resolve;
      try {
        recorder.stop();
      } catch {
        resolve();
      }
      window.setTimeout(resolve, 2000);
    });

    const type = recorder.mimeType || mimeType || "video/webm";
    const blob = new Blob(chunks, { type });
    if (!blob.size) {
      throw new Error("Empty export");
    }
    blob._smartttExt = evalVideoExtension(type);
    return blob;
  } finally {
    try {
      hidden?.pause();
    } catch {
      /* ignore */
    }
    hidden?.remove();
    for (const asset of [logos?.clubLogo, logos?.smartttLogo]) {
      if (asset?.objectUrl) URL.revokeObjectURL(asset.objectUrl);
    }
    canvas.remove();
  }
}
